import { GameObjects } from 'phaser';
import { ARCADE, ARCADE_HEX, ARCADE_TEXT } from '../config/theme';
import { MolduraPixel } from './MolduraPixel';
import { sanitizarCodigo } from '../services/scanPixel';

const ALTURA = 88;
const PASSO = 28;        // distância entre caracteres
const TRACO = 18;        // largura do traço sob cada caractere

const ESTADOS = {
    normal: { fundo: ARCADE_HEX.CAMPO, fio: ARCADE_HEX.FIO_CAMPO, fioMeio: ARCADE_HEX.BRANCO, texto: ARCADE.BRANCO, rotulo: ARCADE.TINTA },
    ok: { fundo: 0xd2ff75, fio: ARCADE_HEX.VERDE, fioMeio: 0xd2ff75, texto: '#29561a', rotulo: '#29561a' },
    erro: { fundo: 0xffa1a1, fio: 0xb55858, fioMeio: 0xffa1a1, texto: '#710909', rotulo: '#710909' }
};

/**
 * Campo de código das fases (Figma, quadros 34–39): caixa escura, um traço
 * por caractere e o rótulo "PISTA N" embaixo. Aceita letras e números
 * (`RN-SP-08`). O <input> DOM fica transparente por cima, só para o teclado
 * do tablet; quem desenha os caracteres é o canvas.
 */
export class CampoCodigo extends GameObjects.Container {
    /** Altura do campo antes da escala (o destaque do enigma embaralhado se mede por ela). */
    static altura () {
        return ALTURA;
    }

    static larguraPara (tamanho) {
        return Math.max(123, 51 + PASSO * tamanho);
    }

    constructor (scene, x, y, config = {}) {
        super(scene, x, y);
        scene.add.existing(this);

        const { tamanho = 2, rotulo = '', aoDigitar = null, aoFocar = null, aoEnter = null } = config;
        this.tamanho = tamanho;
        this.largura = CampoCodigo.larguraPara(tamanho);
        this.estado = 'normal';
        this.focado = false;

        this.moldura = scene.add.graphics();
        this.tracos = scene.add.graphics();
        this.caracteres = Array.from({ length: tamanho }, (_, i) => scene.add.text(
            (i - (tamanho - 1) / 2) * PASSO, -2, 'X', ARCADE_TEXT.codigo
        ).setOrigin(0.5));
        this.rotulo = scene.add.text(0, ALTURA / 2 + 21, rotulo, ARCADE_TEXT.rotuloCodigo).setOrigin(0.5);

        this.elemento = document.createElement('input');
        this.elemento.type = 'text';
        this.elemento.className = 'codigo-arcade';
        this.elemento.maxLength = tamanho;
        this.elemento.autocomplete = 'off';
        this.elemento.autocapitalize = 'characters';
        this.elemento.spellcheck = false;
        this.elemento.setAttribute('autocorrect', 'off');
        this.elemento.setAttribute('aria-label', `Código da ${rotulo.toLowerCase()}`);
        this.elemento.style.width = `${this.largura}px`;
        this.elemento.style.height = `${ALTURA}px`;

        const aoMudar = (evento) => {
            // RI-SP-07: código aprovado não muda mais (teclado, colar ou arrastar).
            if (this.bloqueado) {
                this.elemento.value = this.aprovado;
                return;
            }
            // Teclado virtual com sugestão de palavras: mexer no valor no meio
            // da composição faz o teclado perder ou repetir letras. Então o <input>
            // só é reescrito ao fim da composição; o canvas, que desenha a partir
            // do valor limpo (ver `valor`), acompanha a digitação o tempo todo.
            if (!evento.isComposing) {
                // Só letras e números; digitar limpa o feedback anterior.
                const limpo = sanitizarCodigo(this.elemento.value, tamanho);
                if (limpo !== this.elemento.value) this.elemento.value = limpo;
            }
            this.estado = 'normal';
            this.redesenhar();
            aoDigitar?.(this);
        };
        this.elemento.addEventListener('input', aoMudar);
        this.elemento.addEventListener('compositionend', aoMudar);
        this.elemento.addEventListener('focus', () => {
            this.focado = true;
            this.redesenhar();
            aoFocar?.(this);
        });
        this.elemento.addEventListener('blur', () => {
            this.focado = false;
            this.redesenhar();
        });
        this.elemento.addEventListener('keydown', (evento) => {
            evento.stopPropagation();
            if (evento.key === 'Enter') aoEnter?.(this);
        });

        this.dom = scene.add.dom(0, 0, this.elemento);
        this.add([this.moldura, this.tracos, ...this.caracteres, this.rotulo, this.dom]);
        this.redesenhar();
    }

    /** Marca antes de os filhos serem destruídos: remover o <input> dispara blur. */
    preDestroy () {
        this.destruido = true;
        super.preDestroy();
    }

    /** Valor limpo: durante a composição do teclado o <input> ainda pode ter espaço e símbolos. */
    get valor () { return sanitizarCodigo(this.elemento.value, this.tamanho); }

    /** AP9: devolve ao campo o que estava nele quando o jogador saiu da cena. */
    preencher (valor) {
        this.elemento.value = sanitizarCodigo(valor, this.tamanho);
        this.redesenhar();
        return this;
    }

    /**
     * RI-SP-07: depois de conferido como correto, o campo guarda a resposta e não aceita
     * mais edição nem abre o teclado.
     */
    bloquear () {
        this.bloqueado = true;
        this.aprovado = this.valor;
        this.elemento.readOnly = true;
        this.elemento.tabIndex = -1;
        this.elemento.style.pointerEvents = 'none';
        this.elemento.setAttribute('aria-readonly', 'true');
        this.elemento.blur();
        return this;
    }

    focar () {
        if (this.bloqueado) return this;
        this.elemento.focus({ preventScroll: true });
        return this;
    }

    /** RI-SP-05 / RI-SP-06: feedback visual no próprio campo. */
    setEstado (estado) {
        this.estado = ESTADOS[estado] ? estado : 'normal';
        this.redesenhar();
        return this;
    }

    /**
     * O Phaser só aplica a visibilidade no `display` do <input> no próximo
     * quadro, e um campo com `display: none` recusa o foco. Aplicar na hora
     * permite focar no mesmo toque que fechou o modal — no tablet, é esse
     * toque que autoriza o teclado a abrir.
     */
    setVisivelDom (visivel) {
        this.dom.setVisible(visivel);
        this.elemento.style.display = visivel ? 'block' : 'none';
        return this;
    }

    redesenhar () {
        // O blur do <input> dispara no meio da destruição do campo.
        if (this.destruido || !this.scene) return;
        const cores = ESTADOS[this.estado];
        const valor = this.valor;

        this.moldura.clear();
        MolduraPixel.desenhar(this.moldura, -this.largura / 2, -ALTURA / 2, this.largura, ALTURA, cores,
            { esq: true, dir: true, tamanho: 14 });

        this.tracos.clear();
        this.caracteres.forEach((caractere, i) => {
            const preenchido = i < valor.length;
            // O "X" é só um marcador de posição: fica apagado, e o que foi
            // digitado aparece em cor cheia.
            caractere.setText(preenchido ? valor[i] : 'X').setColor(cores.texto).setAlpha(preenchido ? 1 : 0.3);

            // O traço da próxima posição engrossa enquanto o campo tem foco.
            const proximo = this.focado && i === Math.min(valor.length, this.tamanho - 1);
            this.tracos.fillStyle(parseInt(cores.texto.slice(1), 16), preenchido || proximo ? 1 : 0.45)
                .fillRect(caractere.x - TRACO / 2, 13, TRACO, proximo ? 3 : 1.5);
        });
        this.rotulo.setColor(cores.rotulo);
    }
}
